import { useState, useRef, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { Search, ChevronDown, X, Check, Loader2 } from 'lucide-react';

export interface SearchableOption {
  value: string;
  label: string;
  sublabel?: string;
  icon?: ReactNode;
  group?: string;
  disabled?: boolean;
  meta?: any;
}

interface SearchableSelectProps {
  options: SearchableOption[];
  value: string | null;
  onChange: (value: string | null, option: SearchableOption | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  loading?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  /** When true, fires onSearchChange so parent can do server-side filtering */
  onSearchChange?: (q: string) => void;
  /** Renders a custom row in the dropdown */
  renderOption?: (opt: SearchableOption, active: boolean) => ReactNode;
  /** Custom label for selected display when value is set */
  renderSelected?: (opt: SearchableOption) => ReactNode;
  /** Width of dropdown popover. Defaults to trigger width */
  popoverWidth?: number | string;
  /** id for label association */
  id?: string;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Type to search…',
  emptyMessage = 'No results',
  loading = false,
  clearable = true,
  disabled = false,
  className = '',
  size = 'md',
  onSearchChange,
  renderOption,
  renderSelected,
  popoverWidth,
  id,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value]
  );

  // Filter options client-side unless onSearchChange provided
  const filtered = useMemo(() => {
    if (onSearchChange) return options; // server side
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.sublabel && o.sublabel.toLowerCase().includes(q))
    );
  }, [options, query, onSearchChange]);

  // Group options
  const grouped = useMemo(() => {
    const groups = new Map<string, SearchableOption[]>();
    filtered.forEach((o) => {
      const g = o.group ?? '';
      if (!groups.has(g)) groups.set(g, []);
      groups.get(g)!.push(o);
    });
    return Array.from(groups.entries());
  }, [filtered]);

  // Flat list for keyboard nav
  const flat = filtered;

  useEffect(() => {
    if (!open) {
      setQuery('');
      setActiveIdx(0);
      return;
    }
    // Focus search input
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        !triggerRef.current?.contains(e.target as Node) &&
        !popoverRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Reset active when filtered changes
  useEffect(() => {
    setActiveIdx(0);
  }, [query, options.length]);

  // Scroll active into view
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(
      `[data-idx="${activeIdx}"]`
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, open]);

  const handleSelect = useCallback(
    (opt: SearchableOption) => {
      if (opt.disabled) return;
      onChange(opt.value, opt);
      setOpen(false);
    },
    [onChange]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = flat[activeIdx];
      if (opt) handleSelect(opt);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  const sizes = {
    sm: 'px-2.5 py-1.5 text-xs',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-2.5 text-sm',
  };

  return (
    <div className={`relative ${className}`}>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`flex w-full items-center justify-between gap-2 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-input,var(--bg-surface))] ${sizes[size]} text-left text-[var(--text-primary)] transition-all focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)] disabled:cursor-not-allowed disabled:opacity-60 ${open ? 'border-[var(--ink-0)] ring-1 ring-[var(--ink-0)]' : ''}`}
      >
        <span className="flex min-w-0 flex-1 items-center gap-2">
          {selected ? (
            renderSelected ? (
              renderSelected(selected)
            ) : (
              <>
                {selected.icon}
                <span className="truncate font-medium">{selected.label}</span>
                {selected.sublabel && (
                  <span className="truncate text-xs text-[var(--text-muted)]">
                    {selected.sublabel}
                  </span>
                )}
              </>
            )
          ) : (
            <span className="text-[var(--text-muted)]">{placeholder}</span>
          )}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          {clearable && selected && !disabled && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation();
                onChange(null, null);
              }}
              className="rounded p-0.5 text-[var(--text-muted)] hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-primary)]"
              title="Clear"
            >
              <X size={14} />
            </span>
          )}
          <ChevronDown
            size={16}
            className={`text-[var(--text-muted)] transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </span>
      </button>

      {open && (
        <div
          ref={popoverRef}
          className="absolute z-50 mt-1 overflow-hidden rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] shadow-2xl"
          style={{ width: popoverWidth ?? '100%', minWidth: 240 }}
          onKeyDown={handleKeyDown}
        >
          {/* Search input */}
          <div className="border-b border-[var(--border-primary)] p-2">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
              />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  onSearchChange?.(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                placeholder={searchPlaceholder}
                className="w-full rounded-md border border-transparent bg-[var(--bg-inset,var(--bg-surface-hover))] py-1.5 pl-7 pr-2 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--ink-0)] focus:bg-[var(--bg-surface)] focus:outline-none"
              />
            </div>
          </div>

          {/* Options list */}
          <div ref={listRef} className="max-h-72 overflow-y-auto py-1">
            {loading ? (
              <div className="flex items-center justify-center py-6 text-xs text-[var(--text-muted)]">
                <Loader2 size={14} className="mr-2 animate-spin" />
                Loading…
              </div>
            ) : flat.length === 0 ? (
              <div className="py-6 text-center text-xs text-[var(--text-muted)]">
                {emptyMessage}
              </div>
            ) : (
              grouped.map(([groupName, groupOpts]) => (
                <div key={groupName || 'default'}>
                  {groupName && (
                    <div className="sticky top-0 z-10 bg-[var(--bg-surface)] px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                      {groupName}
                    </div>
                  )}
                  {groupOpts.map((opt) => {
                    const idx = flat.indexOf(opt);
                    const isActive = idx === activeIdx;
                    const isSelected = opt.value === value;
                    if (renderOption) {
                      return (
                        <div
                          key={opt.value}
                          data-idx={idx}
                          onMouseEnter={() => setActiveIdx(idx)}
                          onClick={() => handleSelect(opt)}
                          className={`cursor-pointer ${opt.disabled ? 'pointer-events-none opacity-40' : ''}`}
                        >
                          {renderOption(opt, isActive)}
                        </div>
                      );
                    }
                    return (
                      <div
                        key={opt.value}
                        data-idx={idx}
                        onMouseEnter={() => setActiveIdx(idx)}
                        onClick={() => handleSelect(opt)}
                        className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition-colors ${isActive ? 'bg-[var(--saffron-50)] text-[var(--ink-0)] dark:bg-[var(--ink-0)]/20 dark:text-[var(--saffron-600)]' : 'text-[var(--text-primary)]'} ${opt.disabled ? 'pointer-events-none opacity-40' : ''}`}
                      >
                        {opt.icon}
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">{opt.label}</div>
                          {opt.sublabel && (
                            <div className="truncate text-[11px] text-[var(--text-muted)]">
                              {opt.sublabel}
                            </div>
                          )}
                        </div>
                        {isSelected && (
                          <Check size={14} className="text-[var(--ink-0)]" />
                        )}
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
